import { extendedIdTokenClaims } from '@logto/core-kit';
import { z } from 'zod';
import { accessTokenJwtCustomizerGuard, clientCredentialsJwtCustomizerGuard, } from './jwt-customizer.js';
export * from './oidc-provider.js';
export * from './jwt-customizer.js';
/**
 * Logto OIDC signing key types, used mainly in REST API routes.
 */
export var LogtoOidcConfigKeyType;
(function (LogtoOidcConfigKeyType) {
    LogtoOidcConfigKeyType["PrivateKeys"] = "private-keys";
    LogtoOidcConfigKeyType["CookieKeys"] = "cookie-keys";
})(LogtoOidcConfigKeyType || (LogtoOidcConfigKeyType = {}));
/**
 * Value maps to config key names in `logto_configs` table. Used mainly in DB SQL related scenarios.
 */
export var LogtoOidcConfigKey;
(function (LogtoOidcConfigKey) {
    LogtoOidcConfigKey["PrivateKeys"] = "oidc.privateKeys";
    LogtoOidcConfigKey["CookieKeys"] = "oidc.cookieKeys";
    LogtoOidcConfigKey["Session"] = "oidc.session";
})(LogtoOidcConfigKey || (LogtoOidcConfigKey = {}));
/**
 * Logto supported signing key algorithms for OIDC private keys that sign JWT tokens.
 */
export var SupportedSigningKeyAlgorithm;
(function (SupportedSigningKeyAlgorithm) {
    SupportedSigningKeyAlgorithm["RSA"] = "RSA";
    SupportedSigningKeyAlgorithm["EC"] = "EC";
})(SupportedSigningKeyAlgorithm || (SupportedSigningKeyAlgorithm = {}));
export const oidcConfigKeyGuard = z.object({
    id: z.string(),
    value: z.string(),
    createdAt: z.number(),
});
export const oidcSessionConfigGuard = z.object({
    ttl: z.number().int().min(1).max(31_536_000).optional(),
});
export const logtoOidcConfigGuard = Object.freeze({
    [LogtoOidcConfigKey.PrivateKeys]: oidcConfigKeyGuard.array(),
    [LogtoOidcConfigKey.CookieKeys]: oidcConfigKeyGuard.array(),
    // Session config is optional, if not set, it will fallback to default value in core.
    [LogtoOidcConfigKey.Session]: oidcSessionConfigGuard.nullish().transform((data) => data ?? {}),
});
export var LogtoJwtTokenKey;
(function (LogtoJwtTokenKey) {
    LogtoJwtTokenKey["AccessToken"] = "jwt.accessToken";
    LogtoJwtTokenKey["ClientCredentials"] = "jwt.clientCredentials";
})(LogtoJwtTokenKey || (LogtoJwtTokenKey = {}));
export const jwtCustomizerConfigGuard = Object.freeze({
    [LogtoJwtTokenKey.AccessToken]: accessTokenJwtCustomizerGuard,
    [LogtoJwtTokenKey.ClientCredentials]: clientCredentialsJwtCustomizerGuard,
});
export const jwtCustomizerConfigsGuard = z.discriminatedUnion('key', [
    z.object({
        key: z.literal(LogtoJwtTokenKey.AccessToken),
        value: accessTokenJwtCustomizerGuard,
    }),
    z.object({
        key: z.literal(LogtoJwtTokenKey.ClientCredentials),
        value: clientCredentialsJwtCustomizerGuard,
    }),
]);
/* --- Logto tenant configs --- */
export const adminConsoleDataGuard = z.object({
    signInExperienceCustomized: z.boolean(),
    organizationCreated: z.boolean(),
    developmentTenantMigrationNotification: z
        .object({
        isPaidTenant: z.boolean(),
        /**
         * Tag is used to store the original tenant tag before dev tenant migration.
         * This field is only used for DB rollback and because the `TenantTag` may change, so we don't guard it as the `TenantTag` type.
         */
        tag: z.string(),
        readAt: z.number().optional(),
    })
        .optional(),
    checkedChargeNotification: z
        .object({
        token: z.boolean().optional(),
        apiResource: z.boolean().optional(),
        machineToMachineApp: z.boolean().optional(),
        tenantMember: z.boolean().optional(),
    })
        .optional(),
});
/* --- Logto tenant cloud connection config --- */
export const cloudConnectionDataGuard = z.object({
    appId: z.string(),
    appSecret: z.string(),
    resource: z.string(),
});
/* --- ID Token Config --- */
export const extendedIdTokenClaimsGuard = z.enum(extendedIdTokenClaims);
export const idTokenConfigGuard = z.object({
    enabledExtendedClaims: extendedIdTokenClaimsGuard.array().optional(),
});
export var LogtoTenantConfigKey;
(function (LogtoTenantConfigKey) {
    LogtoTenantConfigKey["AdminConsole"] = "adminConsole";
    LogtoTenantConfigKey["CloudConnection"] = "cloudConnection";
    /** The URL to redirect when session not found in Sign-in Experience. */
    LogtoTenantConfigKey["SessionNotFoundRedirectUrl"] = "sessionNotFoundRedirectUrl";
    /** ID token configuration for extended claims. */
    LogtoTenantConfigKey["IdToken"] = "idToken";
})(LogtoTenantConfigKey || (LogtoTenantConfigKey = {}));
export const logtoTenantConfigGuard = Object.freeze({
    [LogtoTenantConfigKey.AdminConsole]: adminConsoleDataGuard,
    [LogtoTenantConfigKey.CloudConnection]: cloudConnectionDataGuard,
    [LogtoTenantConfigKey.SessionNotFoundRedirectUrl]: z.object({ url: z.string() }),
    [LogtoTenantConfigKey.IdToken]: idTokenConfigGuard,
});
export const logtoConfigKeys = Object.freeze([
    ...Object.values(LogtoOidcConfigKey),
    ...Object.values(LogtoJwtTokenKey),
    ...Object.values(LogtoTenantConfigKey),
]);
export const logtoConfigGuards = Object.freeze({
    ...logtoOidcConfigGuard,
    ...jwtCustomizerConfigGuard,
    ...logtoTenantConfigGuard,
});
export const oidcConfigKeysResponseGuard = oidcConfigKeyGuard
    .omit({ value: true })
    .merge(z.object({ signingKeyAlgorithm: z.nativeEnum(SupportedSigningKeyAlgorithm).optional() }));
